/**
 * Conversion Tracking Utility
 * 
 * Centralized tracking for Google Ads and Meta Pixel events.
 * Supports multiple event types with deduplication.
 * 
 * Events supported:
 * - Purchase: Booking completed with payment
 * - ViewContent: Viewed a service
 * - InitiateCheckout: Started checkout process
 * - Lead: Submitted inquiry/lead form
 * - CompleteRegistration: Signed waiver
 * - Contact: Clicked phone/email
 */

// Load Google Ads script
const loadGoogleAds = (googleAdsId) => {
  if (window.gtag) return Promise.resolve();
  
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${googleAdsId}`;
    script.onload = () => {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      window.gtag('config', googleAdsId);
      resolve();
    };
    document.head.appendChild(script);
  });
};

// Load Meta Pixel script
const loadMetaPixel = (metaPixelId) => {
  if (window.fbq) return Promise.resolve();
  
  return new Promise((resolve) => {
    window.fbq = function() {
      window.fbq.callMethod ? 
        window.fbq.callMethod.apply(window.fbq, arguments) : 
        window.fbq.queue.push(arguments);
    };
    window.fbq.push = window.fbq;
    window.fbq.loaded = true;
    window.fbq.version = '2.0';
    window.fbq.queue = [];
    
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.onload = () => {
      window.fbq('init', metaPixelId);
      resolve();
    };
    document.head.appendChild(script);
  });
};

/**
 * Fire a tracking event to Google Ads and Meta Pixel
 * 
 * @param {string} eventType - Event type: 'Purchase', 'ViewContent', 'InitiateCheckout', 'Lead', 'CompleteRegistration', 'Contact'
 * @param {object} data - Event data
 * @param {string} data.googleAdsId - Google Ads Conversion ID (AW-XXXXXXXXX)
 * @param {string} data.googleAdsLabel - Google Ads Conversion Label
 * @param {string} data.metaPixelId - Meta Pixel ID
 * @param {number} data.value - Conversion value (optional)
 * @param {string} data.currency - Currency code (default: USD)
 * @param {string} data.contentId - Content/Transaction ID
 * @param {string} data.contentName - Name of service/content
 * @param {object} data.customData - Additional custom data
 */
export const trackEvent = async (eventType, data = {}) => {
  const {
    googleAdsId,
    googleAdsLabel,
    metaPixelId,
    value = 0,
    currency = 'USD',
    contentId,
    contentName,
    customData = {}
  } = data;
  
  // Deduplication key (for events that should only fire once)
  const dedupeEvents = ['Purchase', 'Lead', 'CompleteRegistration'];
  if (dedupeEvents.includes(eventType) && contentId) {
    const dedupeKey = `tracking_${eventType.toLowerCase()}_${contentId}`;
    if (localStorage.getItem(dedupeKey)) {
      return { alreadyTracked: true };
    }
  }
  
  const results = { google: false, meta: false };
  
  // ========== GOOGLE ADS ==========
  if (googleAdsId) {
    try {
      await loadGoogleAds(googleAdsId);
      
      // Map event types to Google event names
      const googleEventMap = {
        'Purchase': 'conversion',
        'ViewContent': 'view_item',
        'InitiateCheckout': 'begin_checkout',
        'Lead': 'generate_lead',
        'CompleteRegistration': 'sign_up',
        'Contact': 'contact'
      };
      
      const googleEvent = googleEventMap[eventType] || eventType.toLowerCase();
      
      // Build event params
      const eventParams = {
        'value': value,
        'currency': currency,
        ...customData
      };
      
      // For conversion events, add send_to with label
      if (eventType === 'Purchase' && googleAdsLabel) {
        eventParams['send_to'] = `${googleAdsId}/${googleAdsLabel}`;
        eventParams['transaction_id'] = contentId;
      }
      
      if (contentId) eventParams['item_id'] = contentId;
      if (contentName) eventParams['item_name'] = contentName;
      
      window.gtag('event', googleEvent, eventParams);
      results.google = true;
      
    } catch (error) {
      console.error(`[Tracking] Google Ads error:`, error);
    }
  }
  
  // ========== META PIXEL ==========
  if (metaPixelId) {
    try {
      await loadMetaPixel(metaPixelId);
      
      // Map event types to Meta standard events
      const metaEventMap = {
        'Purchase': 'Purchase',
        'ViewContent': 'ViewContent',
        'InitiateCheckout': 'InitiateCheckout',
        'Lead': 'Lead',
        'CompleteRegistration': 'CompleteRegistration',
        'Contact': 'Contact'
      };
      
      const metaEvent = metaEventMap[eventType] || eventType;
      
      // Build event params
      const eventParams = {
        value: value,
        currency: currency,
        ...customData
      };
      
      if (contentId) eventParams.content_ids = [contentId];
      if (contentName) eventParams.content_name = contentName;
      eventParams.content_type = 'service';
      
      window.fbq('trackSingle', metaPixelId, metaEvent, eventParams);
      results.meta = true;
      
    } catch (error) {
      console.error(`[Tracking] Meta Pixel error:`, error);
    }
  }
  
  // Mark as tracked (for dedupe events)
  if (dedupeEvents.includes(eventType) && contentId && (results.google || results.meta)) {
    const dedupeKey = `tracking_${eventType.toLowerCase()}_${contentId}`;
    localStorage.setItem(dedupeKey, Date.now().toString());
  }
  
  return results;
};

/**
 * Convenience methods for specific events
 */

// Track when someone views a service
export const trackViewContent = (data) => trackEvent('ViewContent', data);

// Track when someone starts checkout
export const trackInitiateCheckout = (data) => trackEvent('InitiateCheckout', data);

// Track when someone submits a lead form
export const trackLead = (data) => trackEvent('Lead', data);

// Track when someone signs a waiver
export const trackCompleteRegistration = (data) => trackEvent('CompleteRegistration', data);

// Track when someone clicks contact (phone/email)
export const trackContact = (data) => trackEvent('Contact', data);

// Track purchase (with backend deduplication)
export const trackPurchase = (data) => trackEvent('Purchase', data);

export default {
  trackEvent,
  trackViewContent,
  trackInitiateCheckout,
  trackLead,
  trackCompleteRegistration,
  trackContact,
  trackPurchase
};
